import React, { useState, useEffect } from 'react';
import HomeScreen from './components/HomeScreen';
import StudentSearch from './components/StudentSearch';
import ReasonSelection from './components/ReasonSelection';
import SuccessScreen from './components/SuccessScreen';
import SetupScreen from './components/SetupScreen';
import LoginScreen from './components/LoginScreen';
import { GoogleSheetsService } from './services/GoogleSheetsService';

function App() {
  const [currentScreen, setCurrentScreen] = useState('login');
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [selectedReason, setSelectedReason] = useState('');
  const [otherReason, setOtherReason] = useState('');
  const [studentsOut, setStudentsOut] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedClass, setSelectedClass] = useState(localStorage.getItem('selectedClass') || null);
  const [sheetsService, setSheetsService] = useState(null);

  useEffect(() => {
    const initializeApp = async () => {
      try {
        // Initialize Google Sheets service
        const service = new GoogleSheetsService();
        await service.initializeAuth();
        
        // Check if user is already authenticated
        const tokenData = localStorage.getItem('google_auth_token');
        if (tokenData) {
          try {
            const parsed = JSON.parse(tokenData);
            const now = Date.now();
            
            if (parsed.expires_at && parsed.expires_at > now) {
              // Token is still valid, restore session
              service.accessToken = parsed.access_token;
              service.refreshToken = parsed.refresh_token;
              service.expiresAt = parsed.expires_at;
              service.isAuthenticated = true;
              
              // Ensure user has a spreadsheet
              await service.ensureUserSpreadsheet();
              
              setSheetsService(service);
              setCurrentScreen('home');
            } else {
              // Token expired, need to re-authenticate
              setCurrentScreen('login');
            }
          } catch (error) {
            console.error('Error parsing stored token:', error);
            setCurrentScreen('login');
          }
        } else {
          // No token found, stay on login screen
          setCurrentScreen('login');
        }
      } catch (error) {
        console.error('Error initializing app:', error);
        setCurrentScreen('login');
      }
    };

    initializeApp();
  }, []);

  // Set up token refresh interval
  useEffect(() => {
    if (!sheetsService) return;

    const tokenRefreshInterval = setInterval(async () => {
      try {
        // Only run token refresh if we're actually on the home screen and need auth
        if (currentScreen === 'home' && sheetsService.isAuthenticationNeeded()) {
          // First try to extend the session silently
          const extended = await sheetsService.extendSession();
          if (!extended) {
            // If silent extension fails, do a full refresh check
            sheetsService.refreshTokenIfNeeded();
          }
        }
      } catch (error) {
        console.error('Error in token refresh cycle:', error);
        // Fallback to regular refresh only if we're on home screen
        if (currentScreen === 'home') {
          sheetsService.refreshTokenIfNeeded();
        }
      }
    }, 2 * 60 * 1000); // Check every 2 minutes

    // Cleanup interval on unmount
    return () => clearInterval(tokenRefreshInterval);
  }, [sheetsService, currentScreen]);

  // Reload students when class changes
  useEffect(() => {
    if (selectedClass && sheetsService && currentScreen === 'home') {
      loadStudentsOut();
    }
  }, [selectedClass, sheetsService, currentScreen]);

  const loadStudentsOut = async () => {
    if (!sheetsService) return;
    
    try {
      setLoading(true);
      console.log('Loading students who are currently out...');
      
      // Debug the spreadsheet structure first
      await sheetsService.debugSpreadsheetStructure();
      
      // Load students from the user's spreadsheet
      const students = await sheetsService.getStudentsOut(selectedClass);
      console.log('Loaded students from spreadsheet:', students);
      
      setStudentsOut(students);
      
      console.log('Students loaded successfully');
    } catch (error) {
      console.error('Error loading students:', error);
      alert('Failed to load students. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleStudentSelect = (student) => {
    setSelectedStudent(student);
    setCurrentScreen('reason-selection');
  };

  const handleReasonSelected = async (reason, reasonDetails) => {
    try {
      setLoading(true);
      setSelectedReason(reason);
      setOtherReason(reasonDetails || '');

      // Record signout to Sheet1
      if (sheetsService && selectedStudent) {
        const exitTime = new Date().toISOString();
        await sheetsService.recordSignout(
          selectedStudent,
          reason,
          reasonDetails || '',
          exitTime,
          selectedClass || ''
        );

        // Preload latest students-out so Home shows updated list immediately
        await loadStudentsOut();
      }

      setCurrentScreen('success');
    } catch (error) {
      console.error('Error recording signout:', error);
      alert('Failed to record signout. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSetup = () => {
    setCurrentScreen('setup');
  };

  const handleClassSelected = (className) => {
    setSelectedClass(className);
    setCurrentScreen('home');
  };

  const handleNewSignout = () => {
    setCurrentScreen('student-search');
  };

  const handleLogout = async () => {
    try {
      // Properly logout from Google OAuth
      if (sheetsService) {
        await sheetsService.logout();
      }
      
      // Clear all stored tokens and data
      localStorage.removeItem('google_auth_token');
      localStorage.removeItem('is_logged_in');
      sessionStorage.removeItem('google_auth_token');
      sessionStorage.removeItem('is_logged_in');
      localStorage.removeItem('selectedClass');
      
      // Reset all app state
      setSheetsService(null);
      setSelectedClass(null);
      setStudentsOut([]);
      setSelectedStudent(null);
      setSelectedReason('');
      
      // Go to login screen
      setCurrentScreen('login');
    } catch (error) {
      console.error('Error during logout:', error);
      // Even if logout fails, clear local state
      setSheetsService(null);
      setCurrentScreen('login');
    }
  };

  const handleLogin = async () => {
    try {
      setLoading(true);
      
      // Initialize Google Sheets service if not already done
      if (!sheetsService) {
        const service = new GoogleSheetsService();
        await service.initializeAuth();
        setSheetsService(service);
      }
      
      // Authenticate user
      const isAuthenticated = await sheetsService.ensureAuthenticated();
      
      if (isAuthenticated) {
        // Ensure user has a spreadsheet (create if new user)
        await sheetsService.ensureUserSpreadsheet();
        
        // Load students who are currently out
        await loadStudentsOut();
        
        setCurrentScreen('home');
      } else {
        alert('Authentication failed. Please try again.');
      }
    } catch (error) {
      console.error('Error during login:', error);
      alert('Login failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const renderScreen = () => {
    switch (currentScreen) {
      case 'login':
        return (
          <LoginScreen 
            onLogin={handleLogin} 
            loading={loading}
            sheetsService={sheetsService}
          />
        );
      case 'home':
        return (
                     <HomeScreen
             studentsOut={studentsOut}
             onStudentSelect={handleStudentSelect}
             onSetup={handleSetup}
             onRefresh={loadStudentsOut}
             onLogout={handleLogout}
             onNewSignout={handleNewSignout}
             selectedClass={selectedClass}
             sheetsService={sheetsService}
           />
        );
      case 'reason-selection':
        return (
          <ReasonSelection
            student={selectedStudent}
            onReasonSelected={handleReasonSelected}
            onBack={() => setCurrentScreen('home')}
            selectedClass={selectedClass}
            sheetsService={sheetsService}
          />
        );
      case 'setup':
        return (
          <SetupScreen
            onBack={() => setCurrentScreen('home')}
            onClassSelected={handleClassSelected}
            sheetsService={sheetsService}
          />
        );
      case 'student-search':
        return (
          <StudentSearch
            onStudentSelected={handleStudentSelect}
            onBack={() => setCurrentScreen('home')}
            sheetsService={sheetsService}
          />
        );
      case 'success':
        return (
          <SuccessScreen
            student={selectedStudent}
            reason={selectedReason}
            otherReason={otherReason}
            onBackToHome={() => setCurrentScreen('home')}
            sheetsService={sheetsService}
          />
        );
      default:
        return (
          <LoginScreen 
            onLogin={handleLogin} 
            loading={loading}
            sheetsService={sheetsService}
          />
        );
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {renderScreen()}
    </div>
  );
}

export default App;
